import { useState, useCallback } from 'react';

interface ReviewTextInputProps {
  value: string;
  onChange: (value: string) => void;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  readonly?: boolean;
}

export function ReviewTextInput({ 
  value, 
  onChange, 
  minLength = 5, 
  maxLength = 200,
  placeholder = "리뷰를 작성해주세요 (5자 이상 200자 이하)",
  readonly = false 
}: ReviewTextInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  }, [onChange, maxLength]);

  const isValid = value.length >= minLength && value.length <= maxLength;
  const characterCount = value.length;

  return (
    <div className="w-full">
      <div className="relative">
        <textarea
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          readOnly={readonly}
          className={`
            w-full p-4 border-2 rounded-xl resize-none h-32
            transition-all duration-200
            ${isFocused 
              ? 'border-green-500 ring-2 ring-green-500 ring-opacity-20' 
              : 'border-gray-300'
            }
            ${!isValid && characterCount > 0 
              ? 'border-red-300 bg-red-50' 
              : ''
            }
            ${readonly 
              ? 'bg-gray-50 cursor-not-allowed' 
              : 'hover:border-gray-400'
            }
            focus:outline-none
          `}
        />
        
        {/* Character Counter */}
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          <span 
            className={`text-sm font-medium ${
              characterCount < minLength 
                ? 'text-red-500' 
                : characterCount > maxLength * 0.9 
                  ? 'text-yellow-600' 
                  : 'text-gray-500'
            }`}
          >
            {characterCount}/{maxLength}
          </span>
        </div>
      </div>

      {/* Validation Messages */}
      <div className="mt-2 min-h-[20px]">
        {characterCount > 0 && !isValid && (
          <p className="text-sm text-red-600">
            {characterCount < minLength 
              ? `최소 ${minLength}자 이상 입력해주세요` 
              : `최대 ${maxLength}자까지 입력 가능합니다`
            }
          </p>
        )}
        {isValid && characterCount >= minLength && (
          <p className="text-sm text-green-600">
            ✓ 리뷰 작성 완료
          </p>
        )}
      </div>
    </div>
  );
}